/**
 * Deterministic colour per procedure name (Activity redesign).
 *
 * Procedures are tenant-defined free text, so we can't hand-map them the way
 * a fixed catalogue would. Instead we hash the (normalised) name onto a small
 * palette of distinct, clinical-friendly hues that read on both the near-white
 * workspace and the dark theme. The same name always lands on the same colour,
 * so after a few sessions operators recognise a procedure by its colour
 * instead of reading the label.
 *
 * "Baseline" is special-cased to a neutral grey — it's not a procedure and
 * shouldn't compete with real procedures for a bright hue.
 */

/** Neutral grey for baselines (and the null/empty fallback). */
export const BASELINE_COLOR = '#78909C';

// Eight distinct hues, all legible on white and reasonably distinguishable for
// the common red-green colour-vision types. Deliberately excludes the exact
// brand-chrome teal so a procedure dot never reads as navigation.
const PALETTE = [
  '#2E6BC9', // blue
  '#7C3AED', // violet
  '#0E9AA5', // teal
  '#F39C12', // orange
  '#C2418E', // magenta
  '#2E9D57', // green
  '#4653C9', // indigo
  '#B5751F', // amber-brown
];

/** Stable string hash (djb2). Small + dependency-free; only needs to spread
 *  names across the palette, not to be cryptographic. */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return h >>> 0; // unsigned
}

/**
 * Colour for a procedure name. `null` / empty / "baseline" → the neutral grey.
 * Everything else is hashed onto the palette.
 */
export function procedureColor(name: string | null | undefined): string {
  if (!name) return BASELINE_COLOR;
  const key = name.trim().toLowerCase();
  if (key === '' || key === 'baseline') return BASELINE_COLOR;
  return PALETTE[hashString(key) % PALETTE.length];
}
